// Coverage — a child's Medicaid eligibility, on their own page.
//
// The baseline decided on 2026-10-24 puts eligibility here rather than on a
// dashboard of its own: what GAMMIS last said, when that was last confirmed,
// twelve months at a glance, and anything that needs acting on. The rules are
// lib/eligibility.js; this only draws them.
//
// What it deliberately does not say: "coverage through" an eligibility period.
// A portal paste or a 271 echoes the date it was asked about, so that date is
// not an end of coverage — until 24 October 2026 this card called almost every
// covered child "lapsed" because of it. Only a real plan end is shown.
import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { coverageProblems, coverageMonths, lastConfirmed } from '../../lib/eligibility';

const parseDay = (iso) => { const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number); return new Date(y, m - 1, d); };
const fmtDay = (iso) => (iso ? format(parseDay(iso), 'MMM d, yyyy') : '—');

const row = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: '0.5px solid var(--border)', gap: 12 };
const labelStyle = { fontSize: 12, color: 'var(--text3)', flexShrink: 0, width: 150 };
const valueStyle = { fontSize: 13, color: 'var(--text)', fontWeight: 500, textAlign: 'right' };

const Row = ({ label, children }) => (
  <div style={row}><span style={labelStyle}>{label}</span><span style={valueStyle}>{children}</span></div>
);

const MONTH_TONE = {
  active: { bg: 'var(--success-bg)', fg: 'var(--success)', word: 'covered' },
  inactive: { bg: 'var(--danger-bg)', fg: 'var(--danger)', word: 'inactive' },
  not_found: { bg: 'var(--warn-bg)', fg: 'var(--warn)', word: 'not found' },
  pending: { bg: 'var(--warn-bg)', fg: 'var(--warn)', word: 'pending' },
};
const UNCHECKED = { bg: 'transparent', fg: 'var(--text3)', word: 'not checked' };

/**
 * @param {object}   p.client   the client row (medicaid_eligible, service_type, eligibility_recheck_date)
 * @param {object[]} p.history  eligibility_checks rows, newest first
 */
export default function CoverageCard({ client, history = [] }) {
  const [showHistory, setShowHistory] = useState(false);
  const [automationOn, setAutomationOn] = useState(null);
  useEffect(() => {
    supabase.rpc('eligibility_automation_on').then(({ data }) => setAutomationOn(data === true));
  }, []);

  const latest = history[0] || null;
  const problems = useMemo(() => coverageProblems({ payer: client.service_type, latest }), [client.service_type, latest]);
  const months = useMemo(() => coverageMonths(history), [history]);
  const confirmed = lastConfirmed(latest);

  const status = client.medicaid_eligible === 'Yes' ? { t: '✓ Eligible', c: 'var(--success)' }
    : client.medicaid_eligible === 'No' ? { t: '✗ Not eligible', c: 'var(--danger)' }
    : { t: '⚠ Not checked', c: 'var(--warn)' };

  const now = new Date();
  const nextCheck = new Date(now.getFullYear(), now.getMonth() + 1, 1);

  return (
    <>
      <Row label="Status"><span style={{ color: status.c, fontWeight: 600 }}>{status.t}</span></Row>
      <Row label="Last confirmed">
        {confirmed ? fmtDay(confirmed) : '—'}
        {latest && (
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>
            {latest.channel === 'edi_270' ? 'automatic 270/271' : latest.checked_by_name || 'GAMMIS portal'}
            {latest.confirmations ? ` · same answer ${latest.confirmations + 1} times since ${fmtDay(latest.checked_at)}` : ''}
          </span>
        )}
      </Row>
      {latest && <Row label="Plan">{latest.managed_care_org || 'Fee-for-service (no CMO)'}</Row>}
      {latest?.coverage_end_is_plan_end && latest.coverage_end && <Row label="Coverage ends">{fmtDay(latest.coverage_end)}</Row>}
      {latest?.aid_category && <Row label="Aid category">{latest.aid_category}</Row>}
      {client.eligibility_recheck_date && <Row label="Re-check date">{fmtDay(client.eligibility_recheck_date)}</Row>}
      <Row label="Next check">
        {automationOn === null ? '…' : automationOn
          ? `${format(nextCheck, 'MMM d, yyyy')} (automatic)`
          : <span style={{ fontWeight: 400, color: 'var(--text3)' }}>Automatic checks start when GAMMIS SFTP is live</span>}
      </Row>

      {problems.length > 0 && (
        <div style={{ margin: '10px 0 4px', padding: '8px 10px', borderRadius: 8, background: 'var(--danger-bg)', border: '0.5px solid var(--danger)' }}>
          {problems.map((p) => (
            <div key={p.kind} style={{ fontSize: 12, color: 'var(--danger)', lineHeight: 1.5 }}>• {p.text[0].toUpperCase() + p.text.slice(1)}</div>
          ))}
        </div>
      )}

      <div style={{ padding: '10px 0 4px' }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Last 12 months</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 3 }}>
          {months.map(({ month, result }) => {
            const tone = MONTH_TONE[result] || UNCHECKED;
            const label = format(parseDay(`${month}-01`), 'MMM yyyy');
            return (
              <div key={month} title={`${label}: ${tone.word}`}
                style={{ height: 26, borderRadius: 4, background: tone.bg, border: `0.5px solid ${result in MONTH_TONE ? tone.fg : 'var(--border2)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, color: tone.fg, minWidth: 0 }}>
                {format(parseDay(`${month}-01`), 'MMMMM')}
              </div>
            );
          })}
        </div>
      </div>

      {history.length > 0 && (
        <div style={{ padding: '8px 0' }}>
          <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => setShowHistory((v) => !v)}>
            {showHistory ? 'Hide' : `${history.length} change${history.length === 1 ? '' : 's'} on record`}
          </button>
          {showHistory && (
            <div style={{ marginTop: 8, border: '0.5px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              {history.map((h) => (
                <div key={h.id} style={{ padding: '8px 10px', borderBottom: '0.5px solid var(--border)', fontSize: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontWeight: 600, color: h.result === 'active' ? 'var(--success)' : h.result === 'inactive' ? 'var(--danger)' : 'var(--warn)' }}>
                      {h.status_text || h.result}
                    </span>
                    <span style={{ color: 'var(--text3)', fontSize: 11 }}>
                      {fmtDay(h.checked_at)}{h.last_confirmed_at ? ` → ${fmtDay(h.last_confirmed_at)}` : ''}
                    </span>
                  </div>
                  <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 2, lineHeight: 1.5 }}>
                    {[
                      Array.isArray(h.change_summary) ? h.change_summary.join('; ') : null,
                      h.managed_care_org,
                      h.aid_category,
                      h.checked_by_name ? `checked by ${h.checked_by_name}` : null,
                      h.channel === 'edi_270' ? 'automatic' : null,
                    ].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {client.eligibility_notes && (
        // Often a pasted portal result with tabs, so it keeps its own layout.
        <div style={{ padding: '8px 0' }}>
          <div style={{ ...labelStyle, width: 'auto', marginBottom: 4 }}>Notes</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'pre-wrap', fontFamily: 'var(--mono)', overflowX: 'auto', tabSize: 4 }}>{client.eligibility_notes}</div>
        </div>
      )}
    </>
  );
}
